import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Eye, HeartHandshake, Scale, ShieldCheck, Users, CheckCircle2 } from "lucide-react";

const siteUrl = "https://www.sevaafoundation.com";
const pageUrl = `${siteUrl}/vision-values`;
const defaultImage = `${siteUrl}/og-blogs.jpg`;
const logoImage = `${siteUrl}/logo512.png`;

const pageTitle = "Vision & Values | SEVAA Foundation";
const pageDescription =
  "Explore SEVAA Foundation's vision and core values of compassion, equity, inclusion, integrity, transparency, community empowerment and accountability.";

const webPageSchema = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  "@id": `${pageUrl}#webpage`,
  name: pageTitle,
  url: pageUrl,
  inLanguage: "en-IN",
  description: pageDescription,
  isAccessibleForFree: true,
  isPartOf: {
    "@type": "WebSite",
    "@id": `${siteUrl}/#website`,
    name: "SEVAA Foundation",
    url: siteUrl,
  },
  publisher: {
    "@type": "NGO",
    "@id": `${siteUrl}/#ngo`,
    name: "SEVAA Foundation",
    alternateName: "Swadesh Sevaa Charitable Foundation",
    url: siteUrl,
    logo: {
      "@type": "ImageObject",
      url: logoImage,
    },
  },
  about: [
    "Vision",
    "Core Values",
    "Compassion",
    "Equity and Inclusion",
    "Integrity",
    "Community Empowerment",
    "Accountability"
  ],
  primaryImageOfPage: {
    "@type": "ImageObject",
    url: defaultImage,
    width: 1200,
    height: 630,
  },
};

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "@id": `${pageUrl}#breadcrumb`,
  itemListElement: [
    {
      "@type": "ListItem",
      position: 1,
      name: "Home",
      item: `${siteUrl}/`,
    },
    {
      "@type": "ListItem",
      position: 2,
      name: "Vision & Values",
      item: pageUrl,
    },
  ],
};


const VisionValues = () => {
  // Data array for Core Values
  const coreValues = [
    {
      id: 1,
      title: "Compassion",
      icon: <HeartHandshake className="w-8 h-8 text-rose-500" />,
      borderColor: "border-rose-100",
      shadowColor: "hover:shadow-rose-500/10",
      content: "We serve with empathy, respect, and humanity toward every individual and community."
    },
    {
      id: 2,
      title: "Equity & Inclusion",
      icon: <Scale className="w-8 h-8 text-amber-500" />,
      borderColor: "border-amber-100",
      shadowColor: "hover:shadow-amber-500/10",
      content: "We believe in equal access to opportunities and rights for all, with special focus on marginalized and tribal populations."
    },
    {
      id: 3,
      title: "Integrity & Transparency",
      icon: <ShieldCheck className="w-8 h-8 text-teal-600" />,
      borderColor: "border-teal-100",
      shadowColor: "hover:shadow-teal-500/10",
      content: "We are committed to ethical conduct, honest communication, and responsible use of resources."
    },
    {
      id: 4,
      title: "Community Empowerment",
      icon: <Users className="w-8 h-8 text-orange-500" />,
      borderColor: "border-orange-100",
      shadowColor: "hover:shadow-orange-500/10",
      content: "We work with communities—not for them—to encourage self-reliance and long-term growth."
    },
    {
      id: 5,
      title: "Accountability",
      icon: <CheckCircle2 className="w-8 h-8 text-emerald-500" />,
      borderColor: "border-emerald-100",
      shadowColor: "hover:shadow-emerald-500/10",
      content: "We hold ourselves accountable to beneficiaries, donors, partners, and society."
    }
  ];

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="title" content={pageTitle} />
        <meta name="description" content={pageDescription} />
        <meta name="robots" content="index, follow, max-image-preview:large" />
        <meta name="author" content="SEVAA Foundation" />
        <meta name="theme-color" content="#0f766e" />
        <meta name="language" content="English, Hindi" />

        <link rel="canonical" href={pageUrl} />

        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="SEVAA Foundation" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:image" content={defaultImage} />
        <meta property="og:image:alt" content="SEVAA Foundation vision and values" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:locale" content="en_IN" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content={defaultImage} />
        <meta name="twitter:image:alt" content="SEVAA Foundation vision and values" />

        <script type="application/ld+json">
          {JSON.stringify(webPageSchema)}
        </script>

        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      </Helmet>

    <div className="bg-slate-50 min-h-screen font-poppins pb-20">
      
      {/* 1. HERO SECTION */}
      <div className="relative w-full h-[400px] md:h-[500px]">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1593113563332-61e860bc808e?q=80&w=2069&auto=format&fit=crop" 
            alt="SEVAA Foundation vision and values for inclusive community development" 
            className="w-full h-full object-cover"
            loading="eager"
            fetchPriority="high"
          />
          {/* Dark gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-r from-teal-900/90 to-black/60"></div>
        </div>

        {/* Hero Content */}
        <div className="relative h-full max-w-7xl mx-auto px-6 lg:px-8 flex flex-col justify-center items-center text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl flex flex-col items-center"
          >
            <div className="w-16 h-16 bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 flex items-center justify-center mb-6 shadow-xl">
              <Eye className="w-8 h-8 text-orange-400" />
            </div>
            <span className="inline-block py-1 px-3 rounded-full bg-orange-500/20 text-orange-400 font-bold text-xs uppercase tracking-widest mb-4 border border-orange-500/30">
              Our Guiding Light
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-white tracking-tight leading-[1.1] mb-6">
              Vision & <span className="text-orange-400">Values</span>
            </h1>
            <p className="text-teal-50 text-base md:text-lg font-medium leading-relaxed max-w-2xl opacity-90">
              The principles that drive us to build a sustainable, equitable, and empowered society.
            </p>
          </motion.div>
        </div>
      </div>

      {/* 2. OUR VISION SECTION (Image + Text Split) */}
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="space-y-6"
          >
            <div className="flex items-center gap-3">
              <span className="w-12 h-[2px] bg-teal-600"></span>
              <span className="text-teal-700 font-bold text-xs uppercase tracking-widest">Our Vision</span>
            </div>
            
            <h2 className="text-3xl md:text-4xl font-black text-slate-900 leading-[1.3]">
              An India where no one is left behind
            </h2>
            
            <p className="text-slate-600 leading-relaxed font-medium text-lg">
              We envision an India where no child is denied education, no family sleeps hungry, no elderly person is left without care, and every individual—regardless of poverty, geography, or background—lives with dignity, health, and equal opportunity.
            </p>
            
            <div className="bg-orange-50 border-l-4 border-orange-500 p-6 rounded-r-2xl mt-8 shadow-sm">
              <p className="text-slate-800 font-bold italic leading-relaxed">
                "SEVAA strives to build inclusive communities where education, healthcare, and food security form the foundation for sustainable social development, especially in tribal and extremely poor regions."
              </p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="relative h-[450px] rounded-3xl overflow-hidden shadow-2xl"
          >
            <img 
              src="https://images.unsplash.com/photo-1488521787991-ed7bbaae773c?q=80&w=2070&auto=format&fit=crop" 
              alt="SEVAA Vision" 
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent flex items-end p-8">
              <p className="text-white font-bold text-xl leading-snug max-w-sm">
                Building a foundation of dignity, health, and equal opportunity.
              </p>
            </div>
          </motion.div>

        </div>
      </div>

      {/* 3. OUR CORE VALUES SECTION (Cards Grid) */}
      <div className="bg-white py-24 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          
          <div className="text-center mb-16 max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-black text-slate-900 mb-4">Our Core Values</h2>
            <p className="text-slate-500 font-medium text-lg">
              These five pillars define who we are, how we work, and what we stand for as an organization.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {coreValues.map((value, index) => (
              <motion.div 
                key={value.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ y: -5 }}
                className={`bg-slate-50 p-8 rounded-[2rem] border ${value.borderColor} transition-all duration-300 hover:shadow-xl ${value.shadowColor} group`}
              >
                <div className="w-16 h-16 bg-white rounded-2xl shadow-sm flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                  {value.icon}
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">{value.title}</h3>
                <p className="text-slate-600 font-medium leading-relaxed">
                  {value.content}
                </p>
              </motion.div>
            ))}
          </div>

        </div>
      </div>

      </div>
    </>
  );
};

export default VisionValues;
